import { Loading3QuartersOutlined } from "@ant-design/icons";
import { Button, Spin, Tag, Form, Col, Row } from "antd";
import { InputNumberField } from "../../../../../../atoms/FormFields";
import { useForm } from "react-hook-form";
import { GetLaborRatesType } from "../../../ducks/types";
import { useEffect, useState } from "react";
import { updateLaborRate } from "../../../ducks/services";
import { SuccessMessage } from "../../../../../../atoms/Toasts/SuccessMessage";
import { AxiosError } from "axios";
import { ErrorMessage } from "../../../../../../atoms/Toasts/ErrorMessage";
import { useAppDispatch } from "../../../../../../config/hooks";
import { getLaborRate } from "../../../ducks/actions";

const antIcon = <Loading3QuartersOutlined spin />;

interface PropsType {
  onClose: () => void;
  data: GetLaborRatesType;
  laborRatesId: string;
  regularCrewSize: number;
}

interface FormValues {
  crew_leader_rate: number;
  technician_rate: number;
}

export default function EditHourlyRates(props: PropsType) {
  const { onClose, data, laborRatesId, regularCrewSize } = props;
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const {
    control,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm<FormValues>();

  useEffect(() => {
    if (data) {
      setValue("crew_leader_rate", data?.crew_leader_rate);
      setValue("technician_rate", data?.technician_rate);
    }
  }, [data, setValue]);

  const onSubmit = (val: FormValues) => {
    setLoading(true);

    const payload = {
      crew_leader_rate: val?.crew_leader_rate,
      technician_rate: val?.technician_rate,
      regular_crew_size: regularCrewSize,
    };

    updateLaborRate(laborRatesId, payload)
      .then(() => {
        setLoading(false);
        SuccessMessage("Labor rates changed");
        dispatch(getLaborRate(companyId));
        onClose();
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  return (
    <Spin indicator={antIcon} size="large" spinning={loading}>
      <div className="modalWrapper view_lhr">
        <div className="modal_header">
          <div className="title">
            <h3>Edit hourly rates for estimates</h3>
          </div>
        </div>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <div className="modal_content">
            <div className="serviceLineTag">
              Service line:
              <span>
                <Tag className="defaultTag light_green_tag">{data?.name}</Tag>
              </span>
            </div>
            <p>
              Specify the crew leader and technician rates and we’ll calculate
              an average.
            </p>

            <Row gutter={16}>
              <Col span={12}>
                <InputNumberField
                  fieldName="crew_leader_rate"
                  label="Crew leader rate"
                  control={control}
                  iProps={{
                    placeholder: "Specify rate",
                    autoComplete: "crew_leader_rate",
                    prefix: "$",
                  }}
                  isRequired
                  rules={{
                    required: "Crew leader rate is required.",
                  }}
                  validate={errors.crew_leader_rate && "error"}
                  validMessage={
                    errors.crew_leader_rate && errors.crew_leader_rate.message
                  }
                  tooltipTitle="Crew leader rate"
                />
              </Col>
              <Col span={12}>
                <InputNumberField
                  fieldName="technician_rate"
                  label="Technician rate"
                  control={control}
                  iProps={{
                    placeholder: "Specify rate",
                    autoComplete: "technician_rate",
                    prefix: "$",
                  }}
                  isRequired={regularCrewSize === 1 ? false : true}
                  rules={{
                    required:
                      regularCrewSize === 1
                        ? false
                        : "Technician rate is required.",
                  }}
                  validate={
                    regularCrewSize === 1
                      ? false
                      : errors.technician_rate && "error"
                  }
                  validMessage={
                    regularCrewSize === 1
                      ? false
                      : errors.technician_rate && errors.technician_rate.message
                  }
                  tooltipTitle="Technician rate"
                />
              </Col>
            </Row>
          </div>

          <div className="modal_footer" style={{ marginTop: 22 }}>
            <Button
              htmlType="button"
              type="default"
              size="large"
              className="outlined-btn"
              onClick={onClose}
            >
              Cancel
            </Button>

            <Button
              htmlType="submit"
              type="primary"
              size="large"
              className="mint-btn"
            >
              Save changes
            </Button>
          </div>
        </Form>
      </div>
    </Spin>
  );
}
